import React from "react";
import { useTheme } from "@mui/material";
import designTokens from "../styles/designTokens";
import "../styles/fonts.css";

interface SocialButtonProps {
  /** `customIcon`이 있으면 이미지 대신 노출 */
  src?: string;
  alt?: string;
  onClick: () => void | Promise<void>;
  children: React.ReactNode;
  darkSrc?: string;
  lightSrc?: string;
  customIcon?: React.ReactNode;
  width?: number | string;
  height?: number;
  fontSize?: number | string;
  horizontalPadding?: number;
  backgroundColor?: string;
}

// Figma "Riquid Glass Dark Mode" effect
const riquidGlassShadow = [
  "inset 1px 1px 0px -0.5px #333333",
  "inset -1px -1px 0px -0.5px #262626",
  "inset 1px 1px 0.5px -1px #FFFFFF",
  "inset -1px -1px 0.5px -1px #FFFFFF",
  "inset 0px 0px 3px 0px rgba(255, 255, 255, 0.5)",
  "inset 0px 0px 16px 0px rgba(242, 242, 242, 0.06)",
  "0px 1px 8px 0px rgba(0, 0, 0, 0.12)",
  "0px 0px 4px 0px rgba(0, 0, 0, 0.1)",
].join(", ");

const riquidGlassHoverShadow = [
  "inset 1px 1px 0px -0.5px #444444",
  "inset -1px -1px 0px -0.5px #333333",
  "inset 1px 1px 0.5px -1px #FFFFFF",
  "inset -1px -1px 0.5px -1px #FFFFFF",
  "inset 0px 0px 4px 0px rgba(255, 255, 255, 0.6)",
  "inset 0px 0px 20px 0px rgba(242, 242, 242, 0.1)",
  "0px 2px 10px 0px rgba(0, 0, 0, 0.18)",
  "0px 0px 6px 0px rgba(0, 0, 0, 0.12)",
].join(", ");

export const SocialButton: React.FC<SocialButtonProps> = ({
  src,
  alt,
  onClick,
  children,
  darkSrc,
  lightSrc,
  customIcon,
  width = 467,
  height = 64,
  fontSize = designTokens.typography.fontSize[20],
  horizontalPadding = 24,
  backgroundColor = designTokens.fill.shape.grey30.dark,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const iconSrc = darkSrc && lightSrc ? (isDark ? lightSrc : darkSrc) : src;

  return (
    <button
      onClick={onClick}
      style={{
        width,
        maxWidth: "100%",
        height,
        borderRadius: 500,
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: `0 ${horizontalPadding}px`,
        backgroundColor,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        color: designTokens.fill.text.common100.dark,
        fontFamily: designTokens.typography.fontFamily.default,
        fontSize,
        fontWeight: 400,
        lineHeight: "23px",
        letterSpacing: "0px",
        border: "none",
        outline: "none",
        cursor: "pointer",
        transition: "all 200ms ease-out",
        boxShadow: riquidGlassShadow,
        overflow: "hidden",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = riquidGlassHoverShadow;
        e.currentTarget.style.backgroundColor = "rgba(42, 42, 45, 0.9)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = riquidGlassShadow;
        e.currentTarget.style.backgroundColor = backgroundColor;
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "scale(0.98)";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <div style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {customIcon ? (
          customIcon
        ) : iconSrc ? (
          <img src={iconSrc} alt={alt ?? ""} style={{ width: 24, height: 24 }} />
        ) : null}
      </div>
      {/*
       * Figma StudioLogin(244:14895)은 [아이콘 32][gap 8][라벨 flex-1 중앙] 구조이고
       * 우측 더미가 없습니다. 더미를 두면 라벨 폭이 32px 줄어, 긴 라벨
       * ("Continue with Studio Code")이 좁은 창에서 두 줄로 깨집니다.
       */}
      <span
        style={{
          flex: "1 0 0",
          textAlign: "center",
          fontSize,
          fontWeight: 400,
          lineHeight: "23px",
          fontFamily: designTokens.typography.fontFamily.default,
        }}
      >
        {children}
      </span>
    </button>
  );
};
